/**
 * @fileoverview Icon Renderer - スキルアイコンのレンダリングヘルパー
 */

import { getSkillDefinition } from '../../gameplay/skills/definitions.js';
import { MinimalErrorTracker } from '../../shared/log.js';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const ARIA_HIDDEN = 'aria-hidden';

/**
 * SVGアイコンを作成
 * @param {Object} iconDef - アイコン定義
 * @param {string} className - CSSクラス名
 * @returns {SVGElement} SVGアイコン要素
 */
function createSVGIcon(iconDef, className) {
  const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
  svg.setAttribute('class', className);
  svg.setAttribute('viewBox', iconDef.viewBox || '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute(ARIA_HIDDEN, 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('xmlns', SVG_NAMESPACE);

  const path = document.createElementNS(SVG_NAMESPACE, 'path');
  path.setAttribute('d', iconDef.path);
  path.setAttribute('fill', 'currentColor');
  svg.appendChild(path);

  return svg;
}

/**
 * 絵文字アイコンを作成
 * @param {Object} iconDef - アイコン定義
 * @param {string} className - CSSクラス名
 * @returns {HTMLSpanElement} 絵文字要素
 */
function createEmojiIcon(iconDef, className) {
  const span = document.createElement('span');
  span.className = className;
  span.textContent = iconDef.emoji || iconDef.fallback || '❓';
  span.setAttribute(ARIA_HIDDEN, 'true');
  span.style.fontSize = '1.5em';
  return span;
}

/**
 * 画像アイコンを作成
 * @param {Object} iconDef - アイコン定義
 * @param {string} className - CSSクラス名
 * @returns {HTMLImageElement} 画像要素
 */
function createImageIcon(iconDef, className) {
  const img = document.createElement('img');
  img.className = className;
  img.src = iconDef.src;
  img.alt = iconDef.alt || '';
  img.setAttribute(ARIA_HIDDEN, 'true');
  return img;
}

/**
 * フォールバックアイコンを作成
 * @param {string} className - CSSクラス名
 * @returns {HTMLSpanElement} フォールバック要素
 */
function createFallbackIcon(className) {
  const span = document.createElement('span');
  span.className = className;
  span.textContent = '❓';
  span.setAttribute(ARIA_HIDDEN, 'true');
  return span;
}

/**
 * スキルアイコンを作成
 * @param {Object} iconDef - アイコン定義（SKILL_DEFINITIONS.ui.icon）
 * @param {string} className - CSSクラス名
 * @returns {HTMLElement} アイコン要素
 */
export function createSkillIcon(iconDef, className = 'skill-icon') {
  if (!iconDef) {
    MinimalErrorTracker.warn('ICON', 'Icon definition is missing, using fallback');
    return createFallbackIcon(className);
  }

  try {
    switch (iconDef.type) {
      case 'svg':
        return createSVGIcon(iconDef, className);
      case 'emoji':
        return createEmojiIcon(iconDef, className);
      case 'image':
        return createImageIcon(iconDef, className);
      default:
        MinimalErrorTracker.warn('ICON', `Unknown icon type: ${iconDef.type}`);
        return createFallbackIcon(className);
    }
  } catch (error) {
    MinimalErrorTracker.error('ICON', 'Failed to create icon', error);
    return createFallbackIcon(className);
  }
}

/**
 * スキルボタンのアイコンを更新
 * @param {string} skillType - スキルタイプ
 */
export function updateSkillButtonIcon(skillType) {
  const skillButton = document.getElementById('skill-button');
  if (!skillButton) {
    MinimalErrorTracker.warn('ICON', 'Skill button element not found');
    return;
  }

  const definition = getSkillDefinition(skillType);
  if (!definition?.ui?.icon) {
    MinimalErrorTracker.warn('ICON', `Icon definition not found for skill: ${skillType}`);
    return;
  }

  try {
    // 既存アイコンを削除
    const oldIcon = skillButton.querySelector('.skill-button__icon');
    if (oldIcon) {
      oldIcon.remove();
    }

    // 新しいアイコンを作成して挿入
    const newIcon = createSkillIcon(definition.ui.icon, 'skill-button__icon');
    skillButton.appendChild(newIcon);

    MinimalErrorTracker.info('ICON', `Skill button icon updated to: ${skillType}`);
  } catch (error) {
    MinimalErrorTracker.error('ICON', 'Failed to update skill button icon', error);
  }
}
